import {Instance} from '@popperjs/core';
import React, {useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {v4 as uuid} from 'uuid';
import {Box, ClickAwayListener, Divider, Stack, alpha} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
  Tooltip,
  DesignSystemThemeProvider,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import translate from '@/utils/translate';
import {contentStudioActions} from '@/stores/contentStudioStore';
import BestPracticesTooltip from '../onboarding-feature-modal/BestPracticesTooltip';
import {onFileInput} from './utils';

type FileUploaderProps = {
  height?: string | number;
  width?: string | number;
  maxWidth?: string | number;
  analyticsLocation: string;
};

function OnboardingFileUploader({
  height,
  width,
  maxWidth,
  analyticsLocation,
}: FileUploaderProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const {uiInteraction} = useUiInteractionEvent();
  const processEventHandlers = useProcessEvent();
  const [uploadInteractionId, setUploadInteractionId] = useState(uuid());
  const popperRef = useRef<Instance>(null);

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop: (acceptedFiles) => {
      onFileInput(
        acceptedFiles,
        uploadInteractionId,
        analyticsLocation,
        processEventHandlers
      );
      setUploadInteractionId(uuid());
      contentStudioActions.setIsOnboardingModalOpen(false);
    },
    // TODO: Export this object to a const when migrating to the new App.
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      'video/webm': ['.webm'],
    },
  });

  const handleImageLoad = () => {
    // Force popper to recalculate position after image loads
    if (popperRef.current) {
      popperRef.current.update();
    }
  };

  return (
    <DesignSystemThemeProvider theme="contentlabLight">
      {/* THE DESIGN SYSTEM PROVIDER IS USED TO IMPORT THE NEW CONTENT LAB THEME, TO BE REMOVED IN THE SEPARATE CL IMPLEMENTATION */}
      <Box
        bgcolor={`${
          isDragActive
            ? designSystemToken('semantic.bg.tertiary')
            : designSystemToken('semantic.bg.secondary')
        }`}
        minHeight={height}
        width={width}
        maxWidth={maxWidth}
        border={`1px dashed ${designSystemToken('semantic.fg.tertiary')}`}
        padding="20px"
        sx={{
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '8px',
        }}
      >
        <div
          {...getRootProps({
            onClick: () => {
              uiInteraction({
                item_name: 'browse_files',
                interaction_location: analyticsLocation,
                interaction_id: uploadInteractionId,
              });
            },
          })}
          className={`dropzone ${isDragActive ? 'active' : ''}`}
          style={{
            display: 'flex',
            flexFlow: 'row nowrap',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <input {...getInputProps()} />
          <Stack
            direction="column"
            margin="0 auto"
            gap="8px"
            alignItems="center"
          >
            <Box
              bgcolor={designSystemToken('semantic.bg.brand-secondary')}
              borderRadius="50%"
              width="48px"
              height="48px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                name="Actions-Upload"
                size="medium"
                appearance="primary"
                style={{alignSelf: 'center'}}
              />
            </Box>
            <Stack direction="row" alignItems="center">
              <Headline size="sm">{translate('Upload your video')}</Headline>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Body size="md">{translate('Max 3 minutes · 1GB per file')}</Body>
            </Stack>
            <Stack
              direction="column"
              alignItems="center"
              bgcolor={alpha(
                designSystemToken('semantic.bg.brand-secondary'),
                0.3
              )}
              borderRadius="8px"
              padding="8px 16px"
              marginY="8px"
            >
              <Body
                size="md"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {translate(
                  'For best results, center key subjects, use a clean '
                )}
              </Body>
              <Stack direction="row" alignItems="center" gap="4px">
                <Body
                  size="md"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {translate('background and ensure stable footage.')}
                </Body>
                <ClickAwayListener
                  onClickAway={() => {
                    setIsTooltipOpen(false);
                  }}
                >
                  <div>
                    <Tooltip
                      open={isTooltipOpen}
                      title={
                        <BestPracticesTooltip
                          onImageLoad={handleImageLoad}
                          onClose={() => setIsTooltipOpen(false)}
                        />
                      }
                      type="light"
                      placement="right"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 10],
                              },
                            },
                          ],
                        },
                      }}
                      PopperProps={{
                        popperRef,
                      }}
                    >
                      <div>
                        <Body
                          size="md"
                          color={designSystemToken('semantic.fg.brand')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsTooltipOpen(!isTooltipOpen);
                          }}
                        >
                          {translate('See best practices')}
                        </Body>
                      </div>
                    </Tooltip>
                  </div>
                </ClickAwayListener>
              </Stack>
            </Stack>
            <Button
              appearance="neutral"
              mode="filled"
              size="medium"
              sx={{width: '271px'}}
            >
              {translate('Browse computer')}
            </Button>
            <Divider flexItem sx={{width: '271px', alignSelf: 'center'}}>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.inverse-secondary')}
              >
                {translate('or')}
              </Body>
            </Divider>
            <Body size="md" color={designSystemToken('semantic.fg.primary')}>
              {translate('Drag and drop video here')}
            </Body>
          </Stack>
        </div>
      </Box>
    </DesignSystemThemeProvider>
  );
}

export default OnboardingFileUploader;
